import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export interface AiConfig {
  provider: string;
  model: string;
  temperature: number;
  persona: string;
  instructions: string;
}

export interface AiSidebarState {
  open: boolean;
  stage: string;
  subStep: string;
  context: any;
  promptTemplate: string;
  targetProperty: string;
  targetComponent: any;
}

const AI_PROVIDERS = {
  openai: {
    name: 'OpenAI',
    models: [
      { id: 'gpt-4o', name: 'GPT-4o' },
      { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
      { id: 'gpt-4-turbo', name: 'GPT-4 Turbo' },
    ]
  }
};

const PERSONAS = [
  { id: 'first_person', name: '1st Person' },
  { id: 'second_person', name: '2nd Person' },
  { id: 'third_person', name: '3rd Person' },
];

const DEFAULT_CONFIG: AiConfig = {
  provider: 'openai',
  model: 'gpt-4o',
  temperature: 0.7,
  persona: 'first_person',
  instructions: '',
};

@Injectable({
  providedIn: 'root'
})
export class CnAiService {

  providers = AI_PROVIDERS;
  personas = PERSONAS;

  // Session config
  config$ = new BehaviorSubject<AiConfig>(this.loadConfig());

  // Sidebar state
  sidebar$ = new BehaviorSubject<AiSidebarState>({
    open: false, stage: '', subStep: '', context: null,
    promptTemplate: '', targetProperty: '', targetComponent: null
  });

  // AI results
  suggestions$ = new BehaviorSubject<string[]>([]);
  loading$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string>('');

  // Cache suggestions per stage-subStep so they persist when reopening
  private suggestionsCache: Map<string, string[]> = new Map();

  private destroy$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  // --- Config Management ---

  private loadConfig(): AiConfig {
    try {
      const saved = sessionStorage.getItem('CN_AI_CONFIG');
      if (saved) {
        return { ...DEFAULT_CONFIG, ...JSON.parse(saved) };
      }
    } catch (e) {}
    return { ...DEFAULT_CONFIG };
  }

  saveConfig(config: AiConfig) {
    this.config$.next(config);
    sessionStorage.setItem('CN_AI_CONFIG', JSON.stringify(config));
  }

  getModelsForProvider(providerId: string): any[] {
    return this.providers[providerId]?.models || [];
  }

  // --- Sidebar Management ---

  openSidebar(stage: string, subStep: string, context: any, promptTemplate: string, targetProperty: string, targetComponent: any) {
    this.error$.next('');
    this.loading$.next(false);

    // Restore cached suggestions for this step, or clear
    const cacheKey = `${stage}-${subStep}`;
    const cached = this.suggestionsCache.get(cacheKey);
    this.suggestions$.next(cached || []);

    this.sidebar$.next({
      open: true, stage, subStep, context,
      promptTemplate, targetProperty, targetComponent
    });
  }

  closeSidebar() {
    const current = this.sidebar$.value;
    this.sidebar$.next({ ...current, open: false });
    this.error$.next('');
    this.loading$.next(false);
  }

  // --- AI Generation (uses Rails open_ai/responses endpoint) ---

  generateSuggestions(customPrompt?: string) {
    const config = this.config$.value;
    const sidebarState = this.sidebar$.value;

    this.loading$.next(true);
    this.error$.next('');
    this.suggestions$.next([]);

    const prompt = customPrompt || this.buildPrompt(sidebarState, config);

    const payload: any = {
      model: config.model,
      input: prompt,
      max_output_tokens: 256,
      temperature: config.temperature
    };

    if (config.instructions?.trim()) {
      payload.instructions = config.instructions;
    }

    this.http.post(`${environment.assessment_admin}api/v1/open_ai/responses.json`, payload)
      .subscribe({
        next: (res: any) => {
          let suggestions: string[] = [];

          // Parse OpenAI Responses API format
          const outputText = this.extractResponseText(res);
          if (outputText) {
            suggestions = outputText.split('\n').filter((s: string) => s.trim() !== '');
          }

          // Clean up numbered prefixes like "1. ", "1) ", "- "
          suggestions = suggestions
            .map((s: string) => s.replace(/^\d+[\.\)]\s*/, '').replace(/^[-•]\s*/, '').trim())
            .filter((s: string) => s.length > 0)
            .slice(0, 5);

          // Cache suggestions for this step
          const key = `${sidebarState.stage}-${sidebarState.subStep}`;
          this.suggestionsCache.set(key, suggestions);

          this.suggestions$.next(suggestions);
          this.loading$.next(false);
        },
        error: (err: any) => {
          let message = 'Something went wrong. Please try again later.';
          if (err?.status === 401) {
            message = 'You are not authorized. Please log in and try again.';
          } else if (err?.status === 422) {
            message = 'The request could not be processed. Please adjust your prompt and try again.';
          } else if (err?.status === 429) {
            message = 'Too many requests. Please wait a moment and try again.';
          } else if (err?.status === 0 || err?.status >= 500) {
            message = 'The AI service is temporarily unavailable. Please try again later.';
          }
          this.error$.next(message);
          this.loading$.next(false);
        }
      });
  }

  private extractResponseText(res: any): string {
    // OpenAI Responses API format: output[].content[].text
    if (res?.output) {
      for (const item of res.output) {
        if (item?.type === 'message' && item?.content) {
          for (const content of item.content) {
            if (content?.type === 'output_text' && content?.text) {
              return content.text;
            }
          }
        }
      }
    }
    // Fallback: direct output_text at top level
    if (res?.output_text) {
      return res.output_text;
    }
    // Fallback: legacy choices format
    if (res?.choices?.[0]?.text) {
      return res.choices[0].text;
    }
    return '';
  }

  private buildPrompt(state: AiSidebarState, config: AiConfig): string {
    let persona = '';
    switch (config.persona) {
      case 'first_person': persona = 'from a first-person perspective'; break;
      case 'second_person': persona = 'from a second-person perspective'; break;
      case 'third_person': persona = 'from a third-person perspective'; break;
    }

    let contextStr = '';
    if (state.context) {
      if (typeof state.context === 'string') {
        contextStr = state.context;
      } else if (Array.isArray(state.context)) {
        contextStr = state.context
          .map((c: any) => {
            if (!c || c === '') return '';
            if (typeof c === 'string') return c;
            if (typeof c === 'object') return c.todo || c.name || c.text || c.content || JSON.stringify(c);
            return String(c);
          })
          .filter((c: string) => c !== '')
          .join(', ');
      } else if (typeof state.context === 'object') {
        contextStr = JSON.stringify(state.context);
      }
    }

    let template = state.promptTemplate || '';
    template = template.replace('{persona}', persona);
    template = template.replace('{context}', contextStr);

    return template;
  }

  // --- Add selected suggestions back to component ---

  addSelectedToComponent(selectedIndices: boolean[]) {
    const state = this.sidebar$.value;
    const suggestions = this.suggestions$.value;

    if (!state.targetComponent || !state.targetProperty) return;

    const selected = suggestions.filter((_, i) => selectedIndices[i]);
    if (selected.length === 0) return;

    const component = state.targetComponent;
    const prop = state.targetProperty;

    // Handle different target property patterns
    if (Array.isArray(component[prop])) {
      const rowCfg = this.getRowConfig(state.stage, state.subStep);
      let itemIndex = 0;

      if (rowCfg && Array.isArray(component[rowCfg.rowArray])) {
        const rows = component[rowCfg.rowArray];

        // Fill empty slots in existing visible rows (index 0 is converge slot, skip it)
        for (let i = 1; i < rows.length && itemIndex < selected.length; i++) {
          if (!component[prop][i] || component[prop][i] === '') {
            component[prop][i] = selected[itemIndex];
            itemIndex++;
          }
        }

        // Create new rows for remaining items, set data at the matching row index
        for (; itemIndex < selected.length; itemIndex++) {
          rows.push(rows.length);
          if (rowCfg.dropdown && rowCfg.defaultDropdown) {
            component[rowCfg.dropdown][rows.length - 1] = rowCfg.defaultDropdown;
          }
          component[prop][rows.length - 1] = selected[itemIndex];
        }
      } else {
        // No row config (develop/implement stages) — fill empty slots then append
        for (let i = 0; i < component[prop].length && itemIndex < selected.length; i++) {
          if (!component[prop][i] || component[prop][i] === '') {
            component[prop][i] = selected[itemIndex];
            itemIndex++;
          }
        }
        for (; itemIndex < selected.length; itemIndex++) {
          component[prop].push(selected[itemIndex]);
        }
      }
    } else if (typeof component[prop] === 'string') {
      if (selected.length === 1) {
        component[prop] = selected[0];
      } else {
        component[prop] = selected.join('\n');
      }
    }
  }

  private getRowConfig(stage: string, subStep: string): any {
    const configs: any = {
      'clarify-goal': { rowArray: 'breakthrough', dropdown: 'breakthrough_dropdown', defaultDropdown: 'It would be great if...' },
      'clarify-data': { rowArray: 'data_dump', dropdown: null, defaultDropdown: null },
      'clarify-challenge': { rowArray: 'question', dropdown: 'question_dropdown', defaultDropdown: 'How might...' },
      'ideate-ideas': { rowArray: 'brainstorm', dropdown: null, defaultDropdown: null },
    };
    return configs[`${stage}-${subStep}`] || null;
  }

  destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
